import axios from 'axios'
import { useAuthStore } from '@/stores/auth'
import { useSnackbarStore } from '@/stores/snackbar'
import router from '@/router'
declare module 'axios' {
  export interface AxiosRequestConfig {
    showSnackbar?: boolean;
  }
}

const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api'

const axiosInstance = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: true, // CORS-related: Allows sending cookies in cross-origin requests
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN'
})

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Ensure credentials are always sent
    config.withCredentials = true
    
    // Ensure headers are properly set for cross-origin requests
    if (config.headers) {
      config.headers['Content-Type'] = 'application/json'
      config.headers['Accept'] = 'application/json'
    }
    
    const token = localStorage.getItem('token')
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]))
       
      } catch (error) {
        console.error('Failed to decode token:', error)
      }
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const code = (error as { code?: string })?.code
    const name = (error as { name?: string })?.name
    if (code === 'ERR_CANCELED' || name === 'CanceledError' || name === 'AbortError') {
      return Promise.reject(error)
    }

    // Get store instance for showing messages
    const snackbarStore = useSnackbarStore()
    const authStore = useAuthStore()

    // Determine if snackbar should be shown (default true)
    const showSnackbar =
      error.config && typeof error.config.showSnackbar !== 'undefined'
        ? error.config.showSnackbar
        : true

    // Handle different error scenarios
    if (error.response) {
      console.error('API Error:', {
        status: error.response.status,
        message: error.response.data?.message,
        url: error.config.url,
        headers: error.config.headers
      })
      
      // Don't show error messages for auth endpoints unless it's a 429
      const isAuthEndpoint = error.config.url.includes('/auth/')
      // Don't show error message for CRA credentials not found
      const isCRACredentialsNotFound = error.response.data?.message === 'CRA credentials not found'
      
      // Handle 401 errors by attempting token refresh
      if (error.response.status === 401 && !error.config._retry && !isAuthEndpoint) {
        error.config._retry = true
        try {
          // Attempt to refresh the token
          const response = await authStore.refreshToken()
          if (response.success && response.accessToken) {
            // Update the failed request's authorization header
            error.config.headers.Authorization = `Bearer ${response.accessToken}`
            // Retry the failed request
            return axiosInstance(error.config)
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError)
          // If refresh fails, handle the failure and redirect to login
          await authStore.handleRefreshFailure()
          // Force navigation to login page
          router.push('/login')
          return Promise.reject(error)
        }
      }
      
      // If the backend sends a specific error message, use it
      if (error.response.data?.message) {
        if ((!isAuthEndpoint || error.response.status === 429) && !isCRACredentialsNotFound) {
          if (showSnackbar) snackbarStore.showError(error.response.data.message)
        }
      } else {
        // Default error messages based on status code
        switch (error.response.status) {
          case 401:
            if (!isAuthEndpoint) {
              if (showSnackbar) snackbarStore.showError('Your session has expired. Please log in again.')
              // Force navigation to login page
              router.push('/login')
            }
            break
          case 403:
            if (showSnackbar) snackbarStore.showError('You do not have permission to perform this action')
            break
          case 404:
            if (!isCRACredentialsNotFound) {
              if (showSnackbar) snackbarStore.showError('The requested resource was not found')
            }
            break
          case 422:
            if (showSnackbar) snackbarStore.showError('Invalid data provided. Please check your input.')
            break
          case 429:
            if (showSnackbar) snackbarStore.showError('Too many requests. Please try again later.')
            break
          case 500:
            if (showSnackbar) snackbarStore.showError('An internal server error occurred. Please try again later.')
            break
          default:
            if (!isAuthEndpoint) {
              if (showSnackbar) snackbarStore.showError('An unexpected error occurred')
            }
        }
      }
    } else if (error.request) {
      // Request was made but no response received
      if (error.code === 'ECONNABORTED') {
        if (showSnackbar) snackbarStore.showError('Request timed out. Please check your connection and try again.')
      } else {
        if (showSnackbar) snackbarStore.showError('Unable to connect to the server. Please check your internet connection.')
      }
    } else {
      // Something happened in setting up the request
      if (showSnackbar) snackbarStore.showError('An error occurred while processing your request')
    }

    return Promise.reject(error)
  }
)

export default axiosInstance 